import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AddIcon from "@mui/icons-material/Add";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import NotesIcon from "@mui/icons-material/Notes";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { toast } from "react-toastify";
import ContentSourcePicker, { formatSourceUrl } from "./ContentSourcePicker";
import LinkIcon from "@mui/icons-material/Link";
import { useReferenceSources } from "./useReferenceSources";
import ImageSourceControl from "./ImageSourceControl";

// Mirror of AiQuizGeneratorModal — same content-picker UX, retargeted at
// swipe deck card generation. Card cap is intentionally tighter (10 vs.
// the quiz endpoint's 50) since long decks lose attention on mobile and
// the BE caps at 10 cards.
const MAX_AI_CARDS = 10;
const DEFAULT_AI_CARDS = 6;

function AiSwipeDeckGeneratorModal(props) {
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 700,
    maxHeight: "98vh",
    bgcolor: "background.paper",
    borderRadius: "16px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    overflowY: "auto",
    border: "none",
  };

  const [numCards, setNumCards] = React.useState(DEFAULT_AI_CARDS);
  const [selectedSections, setSelectedSections] = React.useState([]);
  const [selectedModules, setSelectedModules] = React.useState([]);
  // Documents + folders selection, fetch, and folder→file expansion (shared
  // with the quiz generator and mirroring the AI studio).
  const refs = useReferenceSources(props.homeCompany, props.open);
  const [pastedText, setPastedText] = React.useState("");
  const [customContext, setCustomContext] = React.useState("");
  const [tone, setTone] = React.useState("default");
  // Image-source intent (see ImageSourceControl): "photos_ai" = today's
  // default (reuse real photos from references, AI-generate the rest),
  // "photos" = reuse only, "none" = text-only deck.
  const [imageMode, setImageMode] = React.useState("photos_ai");
  const [pickerOpen, setPickerOpen] = React.useState(false);

  const handleSectionToggle = (sectionUuid) => {
    setSelectedSections((prev) =>
      prev.includes(sectionUuid)
        ? prev.filter((uuid) => uuid !== sectionUuid)
        : [...prev, sectionUuid],
    );
  };

  const handleModuleToggle = (moduleUuid) => {
    setSelectedModules((prev) =>
      prev.includes(moduleUuid)
        ? prev.filter((uuid) => uuid !== moduleUuid)
        : [...prev, moduleUuid],
    );
  };

  const handleGenerate = async () => {
    const hasSource =
      selectedSections.length > 0 ||
      selectedModules.length > 0 ||
      refs.selectedFiles.length > 0 ||
      refs.selectedFolders.length > 0 ||
      pastedText.trim().length > 0;
    if (!hasSource) {
      toast.error(
        "Please add at least one source (section, module, document, or pasted text)",
        { autoClose: 3000 },
      );
      return;
    }

    if (numCards < 1 || numCards > MAX_AI_CARDS) {
      toast.error(`Number of cards must be between 1 and ${MAX_AI_CARDS}`, {
        autoClose: 3000,
      });
      return;
    }

    // Note: parent's onGenerate is fire-and-forget — kicks off the SSE
    // stream and resolves immediately. The editor's chip + storyboard
    // skeletons carry the "generating" feedback once the modal closes.
    try {
      await props.onGenerate({
        sectionUuids: selectedSections,
        moduleUuids: selectedModules,
        fileUuids: refs.expandedFileUuids,
        pastedText: pastedText.trim(),
        numCards,
        customContext,
        tone,
        imageMode,
      });
      props.handleClose();
      // Reset state
      setSelectedSections([]);
      setSelectedModules([]);
      refs.reset();
      setPastedText("");
      setNumCards(DEFAULT_AI_CARDS);
      setCustomContext("");
      setTone("default");
      setImageMode("photos_ai");
    } catch (error) {
      console.error("Error generating swipe deck:", error);
    }
  };

  React.useEffect(() => {
    // Reset transient form state when the parent modal opens (fresh
    // session). Always close the picker on either transition so a
    // close→close-via-X path doesn't leave the sibling picker
    // floating without its parent context.
    if (props.open) {
      setSelectedSections([]);
      setSelectedModules([]);
      refs.reset();
      setPastedText("");
      setNumCards(DEFAULT_AI_CARDS);
      setCustomContext("");
      setTone("default");
      setImageMode("photos_ai");
    }
    setPickerOpen(false);
    // Run only on open/close; refs.reset is stable (useReferenceSources).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const sectionsData = props.sectionsData || [];
  const modulesData = props.modulesData || [];

  // Resolve uuids back to full row objects for chip rendering. Map
  // lookups so 1000-section libraries don't pay an O(n) scan per chip.
  const sectionByUuid = React.useMemo(() => {
    const m = new Map();
    sectionsData.forEach((s) => m.set(s.uuid, s));
    return m;
  }, [sectionsData]);
  const moduleByUuid = React.useMemo(() => {
    const m = new Map();
    modulesData.forEach((mm) => m.set(mm.uuid, mm));
    return m;
  }, [modulesData]);

  const selectedModuleObjects = selectedModules
    .map((u) => moduleByUuid.get(u))
    .filter(Boolean);
  const selectedSectionObjects = selectedSections
    .map((u) => sectionByUuid.get(u))
    .filter(Boolean);
  const pastedSelected = pastedText.trim().length > 0;
  const totalSelected =
    selectedModuleObjects.length +
    selectedSectionObjects.length +
    refs.selectedFolderObjects.length +
    refs.selectedFiles.length +
    (pastedSelected ? 1 : 0);
  const canGenerate = totalSelected > 0 && numCards > 0;

  return (
    <>
    <Modal open={props.open} aria-labelledby="ai-swipe-deck-generator-modal">
      <Box sx={style}>
        <>
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 mx-0 mt-0 mb-6 z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                    <AutoAwesomeIcon
                      className="text-violet-600"
                      style={{ fontSize: "20px" }}
                    />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900">
                      Generate Cards with AI
                    </h3>
                    <p className="text-sm text-slate-600">
                      Select content to use as context for AI-generated cards
                    </p>
                  </div>
                </div>
                <button
                  className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
                  onClick={() => props.handleClose()}
                >
                  <CloseIcon />
                </button>
              </div>
            </div>

            <div className="px-6 space-y-6">
              {/* Number of Cards */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Cards to Generate *
                </label>
                <TextField
                  placeholder={String(DEFAULT_AI_CARDS)}
                  type="number"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={numCards === 0 ? "" : numCards}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "") {
                      setNumCards(0);
                    } else {
                      const parsed = parseInt(value);
                      setNumCards(isNaN(parsed) ? 0 : parsed);
                    }
                  }}
                  InputProps={{
                    inputProps: { min: 1, max: MAX_AI_CARDS },
                    sx: {
                      backgroundColor: "white",
                      borderRadius: "8px",
                      height: "45px",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6d28d9",
                      },
                    },
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Min: 1, Max: {MAX_AI_CARDS}
                </p>
              </div>

              {/* Tone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tone
                </label>
                <select
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-violet-500"
                >
                  <option value="default">Punchy (recommended)</option>
                  <option value="professional">Professional</option>
                  <option value="playful">Playful</option>
                  <option value="serious">Serious / compliance</option>
                </select>
              </div>

              {/* Custom Context */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Instructions (Optional)
                </label>
                <TextField
                  placeholder="E.g. Punchy tone for new hires, focus on safety procedures, include a poll near the end..."
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  size="small"
                  value={customContext}
                  onChange={(e) => setCustomContext(e.target.value)}
                  InputProps={{
                    sx: {
                      backgroundColor: "white",
                      borderRadius: "8px",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6d28d9",
                      },
                    },
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Provide guidance for the AI on what kind of cards to generate
                </p>
              </div>

              {/* Image-source control */}
              <div className="p-3 rounded-lg border border-slate-200">
                <span className="block text-sm font-medium text-gray-700 mb-0.5">
                  Images
                </span>
                <span className="block text-xs text-gray-500 mb-3">
                  Reuse real photos from your reference materials, add
                  AI-generated images, or keep image suggestions only.
                </span>
                <ImageSourceControl
                  value={imageMode}
                  onChange={setImageMode}
                  label=""
                />
              </div>

              {/* Reference content — chip list + picker */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    Reference Content *
                  </label>
                  <span className="text-xs text-slate-500">
                    {totalSelected === 0
                      ? "None selected"
                      : `${totalSelected} selected`}
                  </span>
                </div>

                {totalSelected > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {refs.selectedFolderObjects.map((f) => (
                      <span
                        key={`fld-${f.uuid}`}
                        className="inline-flex items-center gap-1.5 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1"
                      >
                        <FolderOutlinedIcon
                          style={{ fontSize: 13 }}
                          className="text-amber-600"
                        />
                        <span className="font-medium">{f.name}</span>
                        <span className="text-amber-700/70">
                          · {f.file_count} file{f.file_count === 1 ? "" : "s"}
                        </span>
                        <button
                          onClick={() => refs.toggleFolder(f.uuid)}
                          className="ml-0.5 text-amber-700 hover:text-amber-900"
                          aria-label={`Remove ${f.name}`}
                        >
                          <CloseIcon style={{ fontSize: 12 }} />
                        </button>
                      </span>
                    ))}
                    {selectedModuleObjects.map((m) => (
                      <span
                        key={`m-${m.uuid}`}
                        className="inline-flex items-center gap-1.5 text-xs bg-violet-50 border border-violet-200 text-violet-900 rounded px-2 py-1"
                      >
                        <ViewModuleIcon
                          style={{ fontSize: 13 }}
                          className="text-violet-600"
                        />
                        <span className="font-medium">{m.name}</span>
                        <button
                          onClick={() => handleModuleToggle(m.uuid)}
                          className="ml-0.5 text-violet-700 hover:text-violet-900"
                          aria-label={`Remove ${m.name}`}
                        >
                          <CloseIcon style={{ fontSize: 12 }} />
                        </button>
                      </span>
                    ))}
                    {selectedSectionObjects.map((s) => (
                      <span
                        key={`s-${s.uuid}`}
                        className="inline-flex items-center gap-1.5 text-xs bg-white border border-slate-200 text-slate-700 rounded px-2 py-1"
                      >
                        <ViewHeadlineIcon
                          style={{ fontSize: 13 }}
                          className="text-slate-500"
                        />
                        <span className="font-medium">{s.name}</span>
                        <button
                          onClick={() => handleSectionToggle(s.uuid)}
                          className="ml-0.5 text-slate-500 hover:text-slate-700"
                          aria-label={`Remove ${s.name}`}
                        >
                          <CloseIcon style={{ fontSize: 12 }} />
                        </button>
                      </span>
                    ))}
                    {refs.selectedFileObjects.map((f) => {
                      const url = formatSourceUrl(f.source_url);
                      const label = url || f.name || "Document";
                      return (
                        <span
                          key={`f-${f.uuid}`}
                          className="inline-flex items-center gap-1.5 text-xs bg-sky-50 border border-sky-200 text-sky-900 rounded px-2 py-1"
                        >
                          {url ? (
                            <LinkIcon
                              style={{ fontSize: 13 }}
                              className="text-sky-600"
                            />
                          ) : (
                            <DescriptionOutlinedIcon
                              style={{ fontSize: 13 }}
                              className="text-sky-600"
                            />
                          )}
                          <span className="font-medium">{label}</span>
                          <button
                            onClick={() => refs.toggleFile(f.uuid)}
                            className="ml-0.5 text-sky-700 hover:text-sky-900"
                            aria-label={`Remove ${label}`}
                          >
                            <CloseIcon style={{ fontSize: 12 }} />
                          </button>
                        </span>
                      );
                    })}
                    {pastedSelected && (
                      <span className="inline-flex items-center gap-1.5 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1">
                        <NotesIcon
                          style={{ fontSize: 13 }}
                          className="text-amber-600"
                        />
                        <span className="font-medium">Pasted text</span>
                        <button
                          onClick={() => setPastedText("")}
                          className="ml-0.5 text-amber-700 hover:text-amber-900"
                          aria-label="Remove pasted text"
                        >
                          <CloseIcon style={{ fontSize: 12 }} />
                        </button>
                      </span>
                    )}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="w-full px-3 py-2 border border-dashed border-slate-300 hover:border-violet-400 hover:bg-violet-50 text-violet-700 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors"
                >
                  <AddIcon style={{ fontSize: 16 }} />
                  Add reference content
                </button>
                <p className="text-xs text-gray-500 mt-3">
                  AI will use the selected content to generate relevant deck
                  cards. Text cards get an AI-generated photo (or a media
                  suggestion if image generation is off) — you can swap in
                  your own image on any card.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 mx-0 mb-0 mt-8 z-10">
              <div className="flex items-center justify-end gap-3">
                <button
                  className="px-6 py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors"
                  onClick={() => props.handleClose()}
                >
                  Cancel
                </button>
                <button
                  className={`px-6 py-2.5 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                    canGenerate
                      ? "bg-violet-600 hover:bg-violet-700 text-white"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                  onClick={handleGenerate}
                  disabled={!canGenerate}
                >
                  <AutoAwesomeIcon style={{ fontSize: "18px" }} />
                  Generate Cards
                </button>
              </div>
            </div>
          </>
      </Box>
    </Modal>
    {/* Sibling, not child, of the outer Modal — see the same comment
        in AiQuizGeneratorModal for the focus-trap rationale. */}
    <ContentSourcePicker
      open={pickerOpen}
      onClose={() => setPickerOpen(false)}
      companyId={props.homeCompany}
      showPaste={false}
      showFolders
      files={refs.availableFiles}
      folders={refs.availableFolders}
      modules={modulesData}
      sections={sectionsData}
      selectedFileUuids={refs.selectedFiles}
      selectedFolderUuids={refs.selectedFolders}
      selectedModuleUuids={selectedModules}
      selectedSectionUuids={selectedSections}
      pastedText={pastedText}
      onToggleFile={refs.toggleFile}
      onToggleFolder={refs.toggleFolder}
      onToggleModule={handleModuleToggle}
      onToggleSection={handleSectionToggle}
      onPastedTextChange={setPastedText}
      onRefreshFiles={refs.refresh}
    />
    </>
  );
}

export default AiSwipeDeckGeneratorModal;
